import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { ExportConfiguration, ExportPushResult, SummaryRef } from '../types';
import PraktijkdataExportFlow from './PraktijkdataExportFlow';
import EduVExportFlow from './EduVExportFlow';
import MijnKantoorExportFlow from './MijnKantoorExportFlow';
import AfasExportFlow from './AfasExportFlow';
import ZgwExportFlow from './ZgwExportFlow';
import MFilesExportFlow from './MFilesExportFlow';
import HubSpotExportFlow from './HubSpotExportFlow';
import { rememberPraktijkdataConfigs } from '../utils/praktijkdataConfigs';
import { rememberEduVConfigs } from '../utils/eduVConfigs';
import { downloadBlob } from '../utils/downloadFile';
import { summaryMarkdownToClipboardHtml, summaryMarkdownToClipboardMarkdown, summaryMarkdownToPlaintext } from '../utils/summaryClipboard';
import { logger } from '../utils/logger';

interface ShareDialogProps {
    isOpen: boolean;
    onClose: () => void;
    summaryContent: string;
    summaryTitle: string;
    recordingName: string;
    fileId: string;
    meetingType: string;
    /** template_uuid van het verslag; de verslag-endpoints vallen terug op meetingType */
    templateUuid?: string | null;
    /** ISO-datum van het verslag/consult; default voor de Praktijkdata-zoekdatum */
    recordingDate?: string;
}

enum TextFormat {
    MARKDOWN = 'markdown',
    PLAINTEXT = 'plaintext',
    RTF = 'rtf'
}

// Deel-flow voor een custom-webhookexport: kiezen (alleen bij meerdere
// configuraties) → versturen → resultaat tonen.
type WebhookView = 'choose' | 'pushing' | 'success' | 'error';

const ShareDialog: React.FC<ShareDialogProps> = ({
    isOpen,
    onClose,
    summaryContent,
    summaryTitle,
    recordingName,
    fileId,
    meetingType,
    templateUuid,
    recordingDate
}) => {
    const { t } = useTranslation();
    const [selectedFormat, setSelectedFormat] = useState<TextFormat>(TextFormat.RTF);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    // Vluchtige bevestiging onderaan het scherm. Leeft los van de dialoog,
    // zodat de melding blijft staan nadat copyToClipboard onClose() aanroept.
    const [toast, setToast] = useState<string | null>(null);
    // Foutmelding inline in de dialoog (i.p.v. een browser-alert), zodat de
    // gebruiker kan lezen wat misging en het meteen opnieuw kan proberen.
    const [error, setError] = useState<string | null>(null);
    const [webhookConfigs, setWebhookConfigs] = useState<ExportConfiguration[]>([]);
    const [webhookView, setWebhookView] = useState<WebhookView | null>(null);
    const [webhookResult, setWebhookResult] = useState<ExportPushResult | null>(null);
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');
    const [praktijkdataConfigs, setPraktijkdataConfigs] = useState<ExportConfiguration[]>([]);
    const [showPraktijkdata, setShowPraktijkdata] = useState(false);
    const [eduVConfigs, setEduVConfigs] = useState<ExportConfiguration[]>([]);
    const [showEduV, setShowEduV] = useState(false);
    const [mijnKantoorConfigs, setMijnKantoorConfigs] = useState<ExportConfiguration[]>([]);
    const [showMijnKantoor, setShowMijnKantoor] = useState(false);
    const [afasConfigs, setAfasConfigs] = useState<ExportConfiguration[]>([]);
    const [showAfas, setShowAfas] = useState(false);
    const [zgwConfigs, setZgwConfigs] = useState<ExportConfiguration[]>([]);
    const [showZgw, setShowZgw] = useState(false);
    const [mfilesConfigs, setMfilesConfigs] = useState<ExportConfiguration[]>([]);
    const [showMFiles, setShowMFiles] = useState(false);
    const [hubspotConfigs, setHubspotConfigs] = useState<ExportConfiguration[]>([]);
    const [showHubSpot, setShowHubSpot] = useState(false);

    const apiService = new ApiService();
    // Stabiel object: de exportflows hebben het in hun hook-dependencies
    const summaryRef = useMemo<SummaryRef>(() => ({ meetingType, templateUuid }), [meetingType, templateUuid]);

    useEffect(() => {
        if (isOpen) {
            // Vers openen: altijd terug naar het gewone deelmenu.
            setWebhookView(null);
            setWebhookResult(null);
            setShowPraktijkdata(false);
            setShowEduV(false);
            setShowMijnKantoor(false);
            setShowAfas(false);
            setShowZgw(false);
            setShowMFiles(false);
            setShowHubSpot(false);
            setError(null);
            checkExportAvailability();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen verversen bij openen of andere opname/soort
    }, [isOpen, fileId, summaryRef]);

    // Toast vanzelf laten verdwijnen na twee seconden.
    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => setToast(null), 2000);
        return () => clearTimeout(timer);
    }, [toast]);

    const checkExportAvailability = async () => {
        setIsLoading(true);
        try {
            const configurations = await apiService.getExportConfigurations(fileId, summaryRef);
            const webhooks = configurations.filter(config => config.targetPlatform === 'CUSTOM_WEBHOOK');
            setWebhookConfigs(webhooks);
            setSelectedConfigId(webhooks[0]?.id ?? '');
            const praktijkdata = configurations.filter(config => config.targetPlatform === 'PRAKTIJKDATA');
            setPraktijkdataConfigs(praktijkdata);
            // Onthoud deze configuraties (alleen id + naam) zodat de
            // instellingenpagina de medewerker-koppeling kan tonen.
            rememberPraktijkdataConfigs(praktijkdata);
            const eduV = configurations.filter(config => config.targetPlatform === 'EDU_V');
            setEduVConfigs(eduV);
            rememberEduVConfigs(eduV);
            setMijnKantoorConfigs(configurations.filter(config => config.targetPlatform === 'MIJNKANTOOR'));
            setAfasConfigs(configurations.filter(config => config.targetPlatform === 'AFAS'));
            setZgwConfigs(configurations.filter(config => config.targetPlatform === 'ZGW'));
            setMfilesConfigs(configurations.filter(config => config.targetPlatform === 'M_FILES'));
            setHubspotConfigs(configurations.filter(config => config.targetPlatform === 'HUBSPOT'));
        } catch (error) {
            // Configuraties niet op kunnen halen is geen blokkade voor de rest
            // van het deelmenu: de externe exportopties gewoon verbergen.
            logger.error('Error checking export availability:', error);
            setWebhookConfigs([]);
            setPraktijkdataConfigs([]);
            setEduVConfigs([]);
            setMijnKantoorConfigs([]);
            setAfasConfigs([]);
            setZgwConfigs([]);
            setMfilesConfigs([]);
            setHubspotConfigs([]);
        } finally {
            setIsLoading(false);
        }
    };

    const pushToWebhook = async (configId: string) => {
        setSelectedConfigId(configId);
        setWebhookView('pushing');
        try {
            const result = await apiService.pushToCustomWebhook(fileId, summaryRef, configId);
            setWebhookResult(result);
            setWebhookView(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('Error pushing to custom webhook:', error);
            const message = error?.response?.data?.error;
            setWebhookResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.webhook.genericError'),
            });
            setWebhookView('error');
        }
    };

    const openWebhookExport = () => {
        setWebhookResult(null);
        if (webhookConfigs.length > 1) {
            // Meerdere bestemmingen: eerst laten kiezen.
            setWebhookView('choose');
        } else if (webhookConfigs.length === 1) {
            pushToWebhook(webhookConfigs[0].id);
        }
    };

    // Tekst-payload per formaat. Wordt gebruikt voor het native deelvenster
    // (alleen platte tekst) en als plain-text-fallback bij het klembord.
    const getContentForFormat = (format: TextFormat): string => {
        switch (format) {
            case TextFormat.MARKDOWN:
                return summaryMarkdownToClipboardMarkdown(summaryContent);
            case TextFormat.PLAINTEXT:
            case TextFormat.RTF:
                return summaryMarkdownToPlaintext(summaryContent);
            default:
                return summaryMarkdownToClipboardMarkdown(summaryContent);
        }
    };

    // De Web Share API (navigator.share) is op desktopbrowsers onbetrouwbaar:
    // Firefox desktop ondersteunt het niet en in andere browsers verschijnt er
    // vaak geen bruikbaar deelvenster. Alleen op touch-/mobiele apparaten gebruiken.
    const supportsNativeShare = (): boolean => {
        if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
            return false;
        }
        const coarsePointer =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches;
        return (navigator.maxTouchPoints || 0) > 0 && coarsePointer;
    };

    // Zet zowel opgemaakte HTML als platte tekst op het klembord. Een EPD-
    // richtveld pakt de HTML (nette opmaak); een plat tekstveld de fallback.
    // Bij oudere browsers zonder ClipboardItem valt het terug op platte tekst.
    const writeRichTextToClipboard = async (html: string, plain: string): Promise<void> => {
        const canWriteRich =
            typeof ClipboardItem !== 'undefined' &&
            typeof navigator.clipboard?.write === 'function';

        if (canWriteRich) {
            const item = new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([plain], { type: 'text/plain' }),
            });
            await navigator.clipboard.write([item]);
        } else {
            await navigator.clipboard.writeText(plain);
        }
    };

    const copyToClipboard = async () => {
        setError(null);
        try {
            if (selectedFormat === TextFormat.RTF) {
                // Opgemaakte tekst: HTML + platte tekst samen op het klembord.
                await writeRichTextToClipboard(
                    summaryMarkdownToClipboardHtml(summaryContent),
                    summaryMarkdownToPlaintext(summaryContent),
                );
            } else {
                await navigator.clipboard.writeText(getContentForFormat(selectedFormat));
            }

            // Show success message
            const formatNames = {
                [TextFormat.MARKDOWN]: t('shareDialog.formatNames.markdown'),
                [TextFormat.PLAINTEXT]: t('shareDialog.formatNames.plaintext'),
                [TextFormat.RTF]: t('shareDialog.formatNames.rtf')
            };

            setToast(t('shareDialog.copiedToClipboard', { format: formatNames[selectedFormat] }));
            onClose();
        } catch (error) {
            logger.error('Error copying to clipboard:', error);
            setError(t('shareDialog.errors.copyFailed'));
        }
    };

    const exportToPdf = async () => {
        setError(null);
        try {
            const blob = await apiService.downloadPdf(fileId, summaryRef);
            await downloadBlob(blob, `${getSafeFileName(recordingName)}.pdf`);

            onClose();
        } catch (error) {
            logger.error('Error exporting to PDF:', error);
            setError(t('shareDialog.errors.pdfFailed'));
        }
    };

    const exportToWord = async () => {
        setError(null);
        try {
            const blob = await apiService.downloadWord(fileId, summaryRef);
            await downloadBlob(blob, `${getSafeFileName(recordingName)}.docx`);

            onClose();
        } catch (error) {
            logger.error('Error exporting to Word:', error);
            setError(t('shareDialog.errors.wordFailed'));
        }
    };

    const shareToOtherApps = async () => {
        // Op desktop (geen betrouwbare native share) direct kopiëren naar het
        // klembord. Dat is voorspelbaar en geeft via copyToClipboard zichtbare
        // bevestiging, in plaats van een deelvenster dat stil faalt.
        if (!supportsNativeShare()) {
            await copyToClipboard();
            return;
        }

        try {
            await navigator.share({
                title: summaryTitle,
                text: getContentForFormat(selectedFormat),
            });
            onClose();
        } catch (error: any) {
            // Gebruiker heeft het deelvenster zelf geannuleerd → niets doen,
            // niet stilletjes naar het klembord kopiëren.
            if (error?.name === 'AbortError') {
                return;
            }
            logger.error('Error sharing:', error);
            // Echte fout → terugvallen op kopiëren naar klembord.
            await copyToClipboard();
        }
    };

    const getSafeFileName = (fileName: string): string => {
        if (!fileName) return 'verslag_samenvatting';

        // Datum in dag-maand-jaar formaat
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const dateStr = `${day}-${month}-${year}`;

        // Titel verwerken: kleine letters, zonder underscores/spaties
        let processedTitle = fileName
            .toLowerCase()
            .replace(/[<>:"/\\|?*]/g, '') // Verwijder ongeldige karakters
            .replace(/[_\s]+/g, '_') // Vervang underscores en spaties door enkele underscore
            .replace(/^_+|_+$/g, '') // Verwijder underscores aan begin en eind
            .trim();

        // Beperk lengte van titel
        const maxTitleLength = 40;
        if (processedTitle.length > maxTitleLength) {
            processedTitle = processedTitle.substring(0, maxTitleLength).replace(/_+$/, '');
        }

        // Als titel leeg is na verwerking, gebruik standaard naam
        if (!processedTitle) {
            processedTitle = 'verslag';
        }

        // Combineer volgens formaat: {dmy}_{titel}_samenvatting
        return `${dateStr}_${processedTitle}_samenvatting`;
    };

    // Blijf gemount zolang er nog een toast zichtbaar is, ook al is de dialoog
    // zelf al gesloten.
    if (!isOpen && !toast) return null;

    return (
        <>
        {isOpen && (
        // z-[9999]: boven de sticky editor-header (99), toolbar (98) en chat-
        // overlay (110+) van SummaryEditor; zelfde laag als de SupportingDocuments-modal.
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-base font-semibold text-gray-900">{t('common.share')}</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="notice error" role="alert" style={{ margin: '0 0 16px' }}>
                            <p>{error}</p>
                            <button type="button" onClick={() => setError(null)}>{t('common.close')}</button>
                        </div>
                    )}

                    {/* Actieve Praktijkdata-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {showPraktijkdata && (
                        <PraktijkdataExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={praktijkdataConfigs}
                            summaryTitle={summaryTitle}
                            defaultDate={recordingDate}
                            onBack={() => setShowPraktijkdata(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve Edu-V-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && showEduV && (
                        <EduVExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={eduVConfigs}
                            onBack={() => setShowEduV(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve Mijn Kantoor-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && !showEduV && showMijnKantoor && (
                        <MijnKantoorExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={mijnKantoorConfigs}
                            summaryTitle={summaryTitle}
                            onBack={() => setShowMijnKantoor(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve AFAS-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && !showEduV && !showMijnKantoor && showAfas && (
                        <AfasExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={afasConfigs}
                            summaryTitle={summaryTitle}
                            onBack={() => setShowAfas(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve ZGW-flow (opslaan in zaak) vervangt het deelmenu
                        tot de gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && !showEduV && !showMijnKantoor && !showAfas && showZgw && (
                        <ZgwExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={zgwConfigs}
                            summaryTitle={summaryTitle}
                            onBack={() => setShowZgw(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve M-Files-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && !showEduV && !showMijnKantoor && !showAfas && !showZgw && showMFiles && (
                        <MFilesExportFlow
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={mfilesConfigs}
                            summaryTitle={summaryTitle}
                            onBack={() => setShowMFiles(false)}
                            onClose={onClose}
                            onConfigurationsStale={() => {
                                // De configuratie bestaat niet meer: optie
                                // verbergen en de lijst opnieuw ophalen.
                                setShowMFiles(false);
                                setMfilesConfigs([]);
                                checkExportAvailability();
                            }}
                        />
                    )}

                    {!showPraktijkdata && !showEduV && !showMijnKantoor && !showAfas && !showZgw && !showMFiles && showHubSpot && (
                        <HubSpotExportFlow
                            key={`${fileId}/${meetingType}`}
                            fileId={fileId}
                            summaryRef={summaryRef}
                            configs={hubspotConfigs}
                            summaryTitle={summaryTitle}
                            onBack={() => setShowHubSpot(false)}
                            onClose={onClose}
                        />
                    )}

                    {/* Actieve webhook-flow vervangt het deelmenu tot de
                        gebruiker klaar is of teruggaat. */}
                    {!showPraktijkdata && !showEduV && !showMijnKantoor && !showAfas && !showZgw && !showMFiles && !showHubSpot && webhookView && (
                        <div className="space-y-4">
                            {webhookView === 'choose' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('shareDialog.webhook.chooseLabel')}
                                        </label>
                                        <select
                                            value={selectedConfigId}
                                            onChange={(e) => setSelectedConfigId(e.target.value)}
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {webhookConfigs.map((config) => (
                                                <option key={config.id} value={config.id}>{config.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => setWebhookView(null)}
                                            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            {t('shareDialog.webhook.back')}
                                        </button>
                                        <button
                                            onClick={() => pushToWebhook(selectedConfigId)}
                                            className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                        >
                                            {t('shareDialog.webhook.send')}
                                        </button>
                                    </div>
                                </>
                            )}

                            {webhookView === 'pushing' && (
                                <div className="flex flex-col items-center py-8 text-center">
                                    <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                                    <div className="text-sm font-medium text-gray-900">{t('shareDialog.webhook.sending')}</div>
                                    <div className="text-xs text-gray-500 mt-1">{t('shareDialog.webhook.sendingHint')}</div>
                                </div>
                            )}

                            {webhookView === 'success' && (
                                <div className="flex flex-col items-center py-6 text-center">
                                    <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-gray-900">{webhookResult?.message}</p>
                                    <div className="flex space-x-2 mt-4">
                                        {webhookResult?.url && (
                                            <button
                                                onClick={() => window.open(webhookResult.url, '_blank', 'noopener,noreferrer')}
                                                className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                            >
                                                {t('shareDialog.webhook.open')}
                                            </button>
                                        )}
                                        <button
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            {t('shareDialog.webhook.close')}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {webhookView === 'error' && (
                                <div className="flex flex-col items-center py-6 text-center">
                                    <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm text-gray-900">{webhookResult?.message}</p>
                                    <div className="flex space-x-2 mt-4">
                                        <button
                                            onClick={() => pushToWebhook(selectedConfigId)}
                                            className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                                        >
                                            {t('shareDialog.webhook.retry')}
                                        </button>
                                        <button
                                            onClick={() => setWebhookView(null)}
                                            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            {t('shareDialog.webhook.back')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!showPraktijkdata && !showEduV && !showMijnKantoor && !showAfas && !showZgw && !showMFiles && !showHubSpot && !webhookView && (
                    <div className="space-y-3">
                        {/* Share Options */}
                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                                    <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 1 1 0 000 2h1v11H5V5h1a1 1 0 000-2H6z" />
                                </svg>
                            }
                            title={t('shareDialog.options.copyTitle')}
                            subtitle={t('shareDialog.options.copySubtitle')}
                            onClick={copyToClipboard}
                        />

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                </svg>
                            }
                            title={t('shareDialog.options.pdfTitle')}
                            subtitle={t('shareDialog.options.pdfSubtitle')}
                            onClick={exportToPdf}
                        />

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                            }
                            title={t('shareDialog.options.wordTitle')}
                            subtitle={t('shareDialog.options.wordSubtitle')}
                            onClick={exportToWord}
                        />

                        {hubspotConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 20 20" aria-hidden="true">
                                        <circle cx="10" cy="10" r="3" />
                                        <circle cx="4" cy="4" r="1.5" />
                                        <circle cx="16" cy="4" r="1.5" />
                                        <circle cx="4" cy="16" r="1.5" />
                                        <path d="m5 5 3 3m4 0 3-3M5 15l3-3" />
                                    </svg>
                                }
                                title={hubspotConfigs.length === 1
                                    ? t('shareDialog.hubspot.menuTitle', { name: hubspotConfigs[0].name || 'HubSpot' })
                                    : t('shareDialog.hubspot.menuTitleMultiple')}
                                subtitle={t('shareDialog.hubspot.menuSubtitle')}
                                onClick={() => setShowHubSpot(true)}
                            />
                        )}

                        {webhookConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                }
                                title={webhookConfigs.length === 1
                                    ? t('shareDialog.webhook.menuTitle', { name: webhookConfigs[0].name })
                                    : t('shareDialog.webhook.menuTitleMultiple')}
                                subtitle={t('shareDialog.webhook.menuSubtitle')}
                                onClick={openWebhookExport}
                            />
                        )}

                        {praktijkdataConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                    </svg>
                                }
                                title={praktijkdataConfigs.length === 1
                                    ? t('shareDialog.praktijkdata.menuTitle', { name: praktijkdataConfigs[0].name })
                                    : t('shareDialog.praktijkdata.menuTitleMultiple')}
                                subtitle={t('shareDialog.praktijkdata.menuSubtitle')}
                                onClick={() => setShowPraktijkdata(true)}
                            />
                        )}

                        {eduVConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0z" />
                                    </svg>
                                }
                                title={eduVConfigs.length === 1
                                    ? t('shareDialog.eduv.menuTitle', { name: eduVConfigs[0].name })
                                    : t('shareDialog.eduv.menuTitleMultiple')}
                                subtitle={t('shareDialog.eduv.menuSubtitle')}
                                onClick={() => setShowEduV(true)}
                            />
                        )}

                        {mijnKantoorConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
                                        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z" />
                                    </svg>
                                }
                                title={mijnKantoorConfigs.length === 1
                                    ? t('shareDialog.mijnkantoor.menuTitle', { name: mijnKantoorConfigs[0].name })
                                    : t('shareDialog.mijnkantoor.menuTitleMultiple')}
                                subtitle={t('shareDialog.mijnkantoor.menuSubtitle')}
                                onClick={() => setShowMijnKantoor(true)}
                            />
                        )}

                        {afasConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                    </svg>
                                }
                                title={afasConfigs.length === 1
                                    ? t('shareDialog.afas.menuTitle', { name: afasConfigs[0].name })
                                    : t('shareDialog.afas.menuTitleMultiple')}
                                subtitle={t('shareDialog.afas.menuSubtitle')}
                                onClick={() => setShowAfas(true)}
                            />
                        )}

                        {zgwConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10.496 2.132a1 1 0 00-.992 0l-7 4A1 1 0 003 8v7a1 1 0 100 2h14a1 1 0 100-2V8a1 1 0 00.496-1.868l-7-4zM6 9a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1zm3 1a1 1 0 012 0v3a1 1 0 11-2 0v-3zm5-1a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                }
                                title={zgwConfigs.length === 1
                                    ? t('shareDialog.zgw.menuTitle', { name: zgwConfigs[0].name })
                                    : t('shareDialog.zgw.menuTitleMultiple')}
                                subtitle={t('shareDialog.zgw.menuSubtitle')}
                                onClick={() => setShowZgw(true)}
                            />
                        )}

                        {mfilesConfigs.length > 0 && (
                            <ShareOption
                                icon={
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                    </svg>
                                }
                                title={mfilesConfigs.length === 1
                                    ? t('shareDialog.mfiles.menuTitle', { name: mfilesConfigs[0].name })
                                    : t('shareDialog.mfiles.menuTitleMultiple')}
                                subtitle={t('shareDialog.mfiles.menuSubtitle')}
                                onClick={() => setShowMFiles(true)}
                            />
                        )}

                        <ShareOption
                            icon={
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm8 5H8V8l2-2 2 2v2z" clipRule="evenodd" />
                                    <path d="M10 7L8 9h1v2h2V9h1l-2-2z" />
                                </svg>
                            }
                            title={t('shareDialog.options.shareTitle')}
                            subtitle={t('shareDialog.options.shareSubtitle')}
                            onClick={shareToOtherApps}
                        />

                        {/* Loading state for export configurations */}
                        {isLoading && (
                            <div className="flex justify-center py-4">
                                <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                            </div>
                        )}

                        {/* Advanced Options Toggle */}
                        <div className="pt-4 border-t border-gray-200">
                            <button
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className="flex items-center justify-center w-full text-sm text-gray-600 hover:text-primary-600 transition-colors"
                            >
                                <span>{t('shareDialog.advanced')}</span>
                                <svg
                                    className={`w-4 h-4 ml-2 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        {/* Advanced Options */}
                        {showAdvanced && (
                            <div className="pt-4 border-t border-gray-200">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            {t('shareDialog.formatLabel')}
                                        </label>
                                        <div className="flex space-x-2">
                                            {Object.values(TextFormat).map((format) => (
                                                <button
                                                    key={format}
                                                    onClick={() => setSelectedFormat(format)}
                                                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${selectedFormat === format
                                                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                                                        : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    {format === TextFormat.MARKDOWN && 'MD'}
                                                    {format === TextFormat.PLAINTEXT && t('shareDialog.formatButtons.plaintext')}
                                                    {format === TextFormat.RTF && 'RTF'}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    )}
                </div>
            </div>
        </div>
        )}

        {toast && (
            <div className="notizy-toast-host">
                <div className="notizy-toast notizy-toast--success">
                    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {toast}
                </div>
            </div>
        )}
        </>
    );
};

interface ShareOptionProps {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    onClick: () => void;
}

const ShareOption: React.FC<ShareOptionProps> = ({ icon, title, subtitle, onClick }) => {
    return (
        <button
            onClick={onClick}
            className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
        >
            <div className="flex-shrink-0 text-gray-600 mr-3">
                {icon}
            </div>
            <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm">{title}</div>
                <div className="text-gray-500 text-xs">{subtitle}</div>
            </div>
        </button>
    );
};

export default ShareDialog; 
